// ./steps/StepFourDecorations.jsx
import React, { useCallback, useEffect, useState } from 'react';

import CupcakeOptionsPanel from '../components/CupcakeOptionsPanel';
import {
  calcCupcakeAddonsPriceFromUnits,
  summarizeCupcakeFlavors,
  summarizeCupcakeIcing,
  validateCupcakeUnits,
} from '../utils/cupcakeOrderUtils';

import apricot from '../../../images/fillings/apricot.png';
import cherry from '../../../images/fillings/cherry.png';
import fudge from '../../../images/fillings/fudge.gif';
import lemon from '../../../images/fillings/lemon.png';
import pineapple from '../../../images/fillings/pineapple.png';
import raspberry from '../../../images/fillings/raspberry.png';
import strawberry from '../../../images/fillings/strawberry.png';
import bavarian from '../../../images/fillings/Bavarian-Cream.jpg';
import dulce from '../../../images/fillings/dulce.jpg';
import german from '../../../images/fillings/german-chocolate.jpg';

import InfoTooltip from '../../../utils/InfoTooltip';

import dots from '../../../images/decorations/dots.png';
import festive from '../../../images/decorations/festive.png';
import floral from '../../../images/decorations/floral.png';
import scrollAllOver from '../../../images/decorations/scrollallover.png';
import scrollWork from '../../../images/decorations/scrollwork.png';
import swirlsAndDots from '../../../images/decorations/swirlsanddots.png';




const flavorPrices = {
  "White Chocolate": 0, "Chocolate": 0, "German Chocolate": 0, "Amaretto": 5, "Butter Rum": 5,
  "Carrot": 5, "Coconut": 5, "Confetti": 5, "Orange Cream": 5, "Italian Cream": 5, "Lemon": 5,
  "Marble": 5, "Red Velvet": 5, "Spice": 5, "Strawberry": 5, "Yellow": 5,
  "Half & Half (No Specialty)": 5, "Half & Half (1 Specialty)": 10, "Half & Half (2 Specialty)": 15
};

const classicFlavors = [
  "White Chocolate", "Chocolate", "German Chocolate"
];

const premiumFlavors = [
  "Marble", "Red Velvet", "Yellow", "Amaretto", "Butter Rum", "Carrot", "Coconut", "Confetti", "Orange Cream",
  "Italian Cream", "Lemon", "Spice", "Strawberry"
];
const fillingPrices = {
  "No Filling": 0, "Raspberry": 5, "Fudge": 5, "Bavarian Cream": 5, "Strawberry": 5,
  "Dulce de Leche (Caramel)": 5, "Lemon": 5, "Cherry": 5, "Pineapple": 5, "Apricot": 5, "German Chocolate Icing": 5
};

const fillingOptions = [
  { name: "No Filling", img: null, price: 0 },
  { name: "Raspberry", img: raspberry, price: 5 },
  { name: "Fudge", img: fudge, price: 5 },
  { name: "Bavarian Cream", img: bavarian, price: 5 },
  { name: "Strawberry", img: strawberry, price: 5 },
  { name: "Dulce de Leche (Caramel)", img: dulce, price: 5 },
  { name: "Lemon", img: lemon, price: 5 },
  { name: "Cherry", img: cherry, price: 5 },
  { name: "Pineapple", img: pineapple, price: 5 },
  { name: "Apricot", img: apricot, price: 5 },
  { name: "German Chocolate Icing", img: german, price: 5 }
];


const frostingPrices = {
  "White Buttercream": 0, "Cream Cheese": 0, "Chocolate Buttercream": 0,
  "German Chocolate Icing": 0
};

const frostingOptions = [
  {
    name: "White Buttercream",
    color: "#fff5f5",
    desc: "Classic vanilla flavor"
  },
  {
    name: "Cream Cheese",
    color: "#f0ebe7",
    desc: "Rich and tangy"
  },
  {
    name: "Chocolate Buttercream",
    color: "#4e342e",
    desc: "Deep cocoa taste"
  },
  {
    name: "German Chocolate Icing",
    color: "#6d4c41",
    desc: "Nutty coconut-chocolate"
  }

];

const NoDecorationSVG = () => (
  <svg width="60" height="60" viewBox="0 0 100 100" className="mb-1">
    <rect x="20" y="40" width="60" height="30" rx="5" fill="#f8dcdc" />
    <ellipse cx="50" cy="40" rx="30" ry="10" fill="#fdeeee" />
    <line x1="25" y1="25" x2="75" y2="75" stroke="#a62c2b" strokeWidth="4" strokeLinecap="round" />
    <line x1="75" y1="25" x2="25" y2="75" stroke="#a62c2b" strokeWidth="4" strokeLinecap="round" />
  </svg>
);



const decorationOptions = [
  { name: "None", value: "none", componenct: NoDecorationSVG },
  { name: "Festive (with Candy)", value: "festive-candy", img: festive },
  { name: "Festive (without Candy)", value: "festive-no-candy", img: festive },
  { name: "Floral", value: "floral", img: floral },
  { name: "Scroll All Over", value: "scroll-all-over", img: scrollAllOver },
  { name: "Dots", value: "dots", img: dots },
  { name: "Swirls & Dots", value: "swirls-dots", img: swirlsAndDots },
  { name: "Scroll Work", value: "scroll-work", img: scrollWork },
];


// Colors used for decorations
const colorOptions = ["Red", "Pink", "Orange", "Yellow", "Green", "Blue", "Purple", "White", "Black", "Gold", "Silver", "Brown"];
const sugarSprinkleColorOptions = [
  "Red", "Yellow", "Orange", "Pink", "Light Pink", "Blue", "Light Blue", "Dark Blue",
  "Purple", "Light Purple", "Teal", "Black", "Silver", "Gold", "Primary Mix", "Pastel Mix"
];

const BUTTERCREAM_TINT_EXCLUDED_COLORS = ['Silver', 'Gold', 'Primary Mix', 'Pastel Mix'];
const buttercreamTintColorOptions = sugarSprinkleColorOptions.filter(
  (c) => !BUTTERCREAM_TINT_EXCLUDED_COLORS.includes(c)
);

const ribbonPrices = { none: 0, fabric: 5, fondant: 20 };

const topperPrices = {
  None: 0, "Plain text": 0, Provided: 0, "2D Pre-Cut Message": 15,
  "Custom Cake Topper": 20
};


const topperColorFinishOptions = [
  ...["Black", "Pink", "Blue", "Silver", "Gold", "Green", "Maroon"].flatMap(color => [
    { label: `Matte ${color}`, value: `Matte ${color}`, color, finish: "Matte" },
    { label: `Glittery ${color}`, value: `Glittery ${color}`, color, finish: "Glittery" },
  ]),
  { label: `Glittery White`, value: `Glittery White`, color: "White", finish: "Glittery" }, // White only glittery
];


const CakeFillingSVG = () => (
  <svg width="80" height="60" viewBox="0 0 100 60" className="mb-2">
    {/* Top Cake Layer */}
    <rect x="10" y="20" width="80" height="10" rx="4" fill="#f7c8c8" />

    {/* Filling Layer (Thin and tight between layers) */}
    <rect x="10" y="30" width="80" height="5" rx="2.5" fill="#e57474" />

    {/* Bottom Cake Layer - starts right after filling */}
    <rect x="10" y="35" width="80" height="10" rx="4" fill="#f7c8c8" />
  </svg>
);



const FrostingSVG = () => (
  <svg width="100" height="80" viewBox="0 0 120 80" className="mb-2">
    {/* Frosting Drip */}
    <path
      d="M10,40 
         Q20,20 30,40 
         Q40,60 50,40 
         Q60,20 70,40 
         Q80,60 90,40 
         Q100,20 110,40 
         L110,30 
         Q60,5 10,30 Z"
      fill="#f78b8b"
    />

    {/* Top Frosting Layer (cap) */}
    <ellipse cx="60" cy="30" rx="50" ry="15" fill="#f5b5b5" />

    {/* Cake Base */}
    <rect x="20" y="40" width="80" height="25" rx="8" fill="#fdd9d9" />
    <rect x="20" y="50" width="80" height="5" fill="#f5b5b5" opacity="0.3" />
  </svg>
);



const StepFourDecorations = ({ formData, setFormData, nextStep, prevStep }) => {
  const isCupcakes = formData.cakeShape === 'Cupcakes';
  const [extraFlavors, setExtraFlavors] = useState([]);
  const topperType = formData.cakeTopper?.split(":")[0] || "";
  const customTopperText = topperType === "Plain Text" || topperType === "Custom Cake Topper"
    ? formData.cakeTopper?.split(": ")[1]?.split(" (")[0] || ""
    : "";

  const [error, setError] = useState('');
  const skipValidation = false; // ⬅️ Turn this true to skip validation while developing
  const [dotsColors, setDotsColors] = useState([]); // Stores selected colors (max 2)
  const [cupcakeUnitConfigs, setCupcakeUnitConfigs] = useState(
    () => formData.cupcakeUnitConfigs || []
  );
  const [cupcakeConfigMode, setCupcakeConfigMode] = useState(
    () => formData.cupcakeConfigMode || 'uniform'
  );
  const handleCupcakeConfigsChange = useCallback(({ unitConfigs, configMode }) => {
    setCupcakeUnitConfigs(unitConfigs);
    setCupcakeConfigMode(configMode);
  }, []);


  const toggleFlavor = (flavor) => {
    setExtraFlavors((prev) =>
      prev.includes(flavor)
        ? prev.filter((f) => f !== flavor)
        : [...prev, flavor]
    );
  };


  // Inside the StepFourDecorations component:

  const [floralType, setFloralType] = useState("");
  const [scrollColor, setScrollColor] = useState("");
  const [dotsColor1, setDotsColor1] = useState("");
  const [dotsColor2, setDotsColor2] = useState("");
  const [topperColorFinish, setTopperColorFinish] = useState("");



  const handleDotsColorCheckbox = (color) => {
    setDotsColors((prev) => {
      if (prev.includes(color)) return prev.filter((c) => c !== color); // remove
      if (prev.length < 2) return [...prev, color]; // add
      return prev; // block extra
    });
  };
  const setFloralDecoration = (val) => {
    setFloralType(val);
    setFormData((prev) => ({ ...prev, decorationStyle: `floral:${val}` }));
  };

  const setScrollColorDecoration = (val) => {
    setScrollColor(val);
    const base = formData.decorationStyle.split(":")[0] || "scroll-all-over";
    setFormData((prev) => ({ ...prev, decorationStyle: `${base}:${val}` }));
  };

  const setSwirlsDotsDecoration = (dots, swirls) => {
    if (dots && swirls) {
      setFormData((prev) => ({
        ...prev,
        decorationStyle: `swirls-dots: swirls(${swirls})+dots(${dots})`,
      }));
    }
  };


  useEffect(() => {
    const mainFlavor = formData.baseCakeFlavor || "";
    const extras = extraFlavors.join(", ");
    const finalFlavor = mainFlavor.startsWith("Half & Half")
      ? `${mainFlavor}${extras ? `: ${extras}` : ""}`
      : mainFlavor;

    // Only update if cakeFlavor actually changes
    if (formData.cakeFlavor !== finalFlavor) {
      setFormData((prev) => ({ ...prev, cakeFlavor: finalFlavor }));
    }

    // Reset extraFlavors only if mainFlavor is not Half & Half AND extraFlavors is not already empty
    if (!mainFlavor.startsWith("Half & Half") && extraFlavors.length > 0) {
      setExtraFlavors([]);
    }
  }, [formData.baseCakeFlavor, formData.cakeFlavor, extraFlavors, setFormData]);

  useEffect(() => {
    const hideHalfAndHalf = (formData.cakeShape === 'Round' || formData.cakeShape === 'Square') && formData.cakeSize === '8" single layer';
    if (hideHalfAndHalf && formData.baseCakeFlavor?.startsWith("Half & Half")) {
      setExtraFlavors([]);
      setFormData((prev) => ({ ...prev, baseCakeFlavor: "" }));
    }
  }, [formData.cakeShape, formData.cakeSize, formData.baseCakeFlavor, setFormData]);


  useEffect(() => {
    if (isCupcakes) {
      const cupcakeAddonsPrice = calcCupcakeAddonsPriceFromUnits(cupcakeUnitConfigs);
      const basePrice = Number(formData.baseCakePrice || 0);
      const allFlavors = [...new Set(cupcakeUnitConfigs.flatMap((u) => u.flavors || []))];
      const allIcing = [...new Set(cupcakeUnitConfigs.flatMap((u) => u.icingSelections || []))];
      const allSprinkles = [...new Set(cupcakeUnitConfigs.flatMap((u) => u.sugarSprinkleColors || []))];
      setFormData((prev) => ({
        ...prev,
        fillingFlavor: 'No Filling',
        decorationStyle: 'Cupcake Sprinkles',
        cakeTopper: 'N/A for cupcakes',
        cakeFlavor: summarizeCupcakeFlavors(cupcakeUnitConfigs),
        frosting: summarizeCupcakeIcing(cupcakeUnitConfigs),
        cupcakeUnitConfigs,
        cupcakeConfigMode,
        cupcakeFlavorSelections: allFlavors,
        icingSelections: allIcing,
        sugarSprinkleColors: allSprinkles,
        cupcakeIcingTintEnabled: cupcakeUnitConfigs.some((u) => u.icingTintEnabled),
        cupcakeIcingTintColor: cupcakeUnitConfigs.find((u) => u.icingTintColor)?.icingTintColor || '',
        decorations_price: cupcakeAddonsPrice.toFixed(2),
        price: (basePrice + cupcakeAddonsPrice).toFixed(2),
      }));
      return;
    }
    const basePrice = parseFloat(formData.baseCakePrice) || 0;

    const topperType = formData.cakeTopper?.split(":")[0]?.trim();
    let topperPrice = topperPrices[topperType] || 0;

    if (topperType === "Custom Cake Topper") {
      const wordCount = customTopperText.trim().split(/\s+/).length;
      if (wordCount >= 4) topperPrice = wordCount === 4 ? 25 : 30;
    }

    const decorations =
      (flavorPrices[formData.baseCakeFlavor] || 0) +
      (Number(formData.servings) > 50 && flavorPrices[formData.baseCakeFlavor] > 0 ? 5 : 0) +
      (fillingPrices[formData.fillingFlavor] || 0) +
      (frostingPrices[formData.frosting] || 0) +
      (ribbonPrices[formData.ribbonOption] || 0) +
      topperPrice +
      (Number(formData.servings) > 50 && formData.fillingFlavor !== "No Filling" ? 5 : 0)
      - (!formData.fillingFlavor ? 5 : 0)
      ;


    console.log("Flavor Base:", flavorPrices[formData.baseCakeFlavor] || 0);
    console.log(
      "Flavor Extra (>50 servings):",
      Number(formData.servings) > 50 && flavorPrices[formData.baseCakeFlavor] > 0 ? 5 : 0
    );
    console.log("Filling Base:", fillingPrices[formData.fillingFlavor] || 0);
    console.log("Frosting:", frostingPrices[formData.frosting] || 0);
    console.log("Ribbon:", ribbonPrices[formData.ribbonOption] || 0);
    console.log("Topper:", topperPrice);
    console.log(
      "Filling Extra (>50 servings):",
      Number(formData.servings) > 50 &&
         formData.fillingFlavor !== "No Filling"
        ? 5
        : 0
    );

    setFormData((prev) => ({
      ...prev,
      price: (basePrice + decorations).toFixed(2),
      decorations_price: decorations.toFixed(2)
    }));
  }, [
    isCupcakes,
    formData.cupcakeDozens,
    formData.baseCakePrice,
    formData.baseCakeFlavor,
    formData.servings,
    formData.fillingFlavor,
    formData.frosting,
    formData.ribbonOption,
    formData.cakeTopper,
    customTopperText,
    cupcakeUnitConfigs,
    cupcakeConfigMode,
    setFormData
  ]);


  useEffect(() => {
    if (formData.decorationStyle.startsWith("dots")) {
      const [primary, secondary] = dotsColors;
      if (primary) {
        const value = `dots:${primary}${secondary ? "+" + secondary : ""}`;
        setFormData((prev) => ({ ...prev, decorationStyle: value }));
      }
    }
  }, [dotsColors, formData.decorationStyle, setFormData]);

  useEffect(() => {
    if (topperType === "2D Pre-Cut Message") {
      const selectedMessage = formData.cakeTopper?.split(": ")[1]?.split(" (")[0] || "";
      if (selectedMessage) {
        const nextCakeTopper = `2D Pre-Cut Message: ${selectedMessage}${topperColorFinish ? ` (${topperColorFinish})` : ""}`;
        setFormData((prev) => (prev.cakeTopper === nextCakeTopper ? prev : { ...prev, cakeTopper: nextCakeTopper }));
      }
    } else if (topperType === "Custom Cake Topper" && customTopperText.trim()) {
      const nextCakeTopper = `Custom Cake Topper: ${customTopperText}${topperColorFinish ? ` (${topperColorFinish})` : ""}`;
      setFormData((prev) => (prev.cakeTopper === nextCakeTopper ? prev : { ...prev, cakeTopper: nextCakeTopper }));
    }
  }, [topperType, customTopperText, topperColorFinish, formData.cakeTopper, setFormData]);


  const getFlavorType = (flavor) => {
    if (classicFlavors.includes(flavor)) return "classic";
    if (premiumFlavors.includes(flavor)) return "premium";
    return null;
  };

  const allowedExtrasMap = {
    "Half & Half (No Specialty)": { classic: 2, premium: 0 },
    "Half & Half (1 Specialty)": { classic: 1, premium: 1 },
    "Half & Half (2 Specialty)": { classic: 0, premium: 2 },
  };

  const hideHalfAndHalf = (formData.cakeShape === 'Round' || formData.cakeShape === 'Square') && formData.cakeSize === '8" single layer';

  const allowedExtras = allowedExtrasMap[formData.baseCakeFlavor] || { classic: 0, premium: 0 };

  const flavorCounts = extraFlavors.reduce(
    (acc, flavor) => {
      const type = getFlavorType(flavor);
      if (type) acc[type]++;
      return acc;
    },
    { classic: 0, premium: 0 }
  );

  const isExtraDisabled = (flavor) => {
    if (extraFlavors.includes(flavor)) return false;
    const type = getFlavorType(flavor);
    if (!type) return true;

    return flavorCounts[type] >= allowedExtras[type];
  };

  return (
    <div className="px-2 sm:px-4 py-4 sm:py-6 max-w-4xl mx-auto w-full min-w-0 overflow-x-hidden">
      <h2 className="text-center text-xl sm:text-2xl md:text-3xl font-bold text-candy-text font-hobo mb-4 sm:mb-6">
        {isCupcakes ? 'Cupcake Options' : 'Cake Decorations'}
      </h2>

      {isCupcakes && (
        <div className="w-full min-w-0 max-w-full overflow-x-hidden">
          <CupcakeOptionsPanel
            totalDozens={Number(formData.cupcakeDozens || 0)}
            initialConfigs={cupcakeUnitConfigs}
            formData={formData}
            onConfigsChange={handleCupcakeConfigsChange}
            sugarSprinkleColorOptions={sugarSprinkleColorOptions}
            buttercreamTintColorOptions={buttercreamTintColorOptions}
          />
        </div>
      )}

      {!isCupcakes && (
      <>
      {/* Cake Flavor */}
      <div className="mb-6">
        <div className="flex items-center gap-2 mb-2">
          <h3 className="block font-semibold text-gray-700">Cake Flavor</h3>
          <InfoTooltip>
            <p className="text-center text-[11px] leading-snug">
              Choose your main cake flavor. Half & Half options let you select multiple.
            </p>
          </InfoTooltip>
        </div>

        <div className="relative">
          <select
            name="baseCakeFlavor"
            value={formData.baseCakeFlavor || ""}
            onChange={(e) =>
              setFormData((prev) => ({ ...prev, baseCakeFlavor: e.target.value }))
            }
            required
            className={`appearance-none w-full p-3 border border-candy-border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-candy-primary bg-white shadow-sm 'text-gray-900'
}`}

          >
            <option value="" disabled hidden style={{ color: '#9ca3af' }}>Select a cake flavor</option>

            <optgroup label="Classic Flavors">
              {["White Chocolate", "Chocolate", "German Chocolate"].map((flavor) => (
                <option key={flavor} value={flavor}>{flavor}</option>
              ))}
            </optgroup>

            <optgroup label={`Premium Flavors`}>
              {premiumFlavors.map((flavor) => (
                <option key={flavor} value={flavor}>
                  {flavor} (+${5 + (Number(formData.servings) > 50 ? 5 : 0)})
                </option>
              ))}
            </optgroup>


            {!hideHalfAndHalf && (
            <optgroup label="Half & Half Options">
              {[
                { label: "Half & Half (No Specialty)", base: 5 },
                { label: "Half & Half (1 Specialty)", base: 10 },
                { label: "Half & Half (2 Specialty)", base: 15 },
              ].map(({ label, base }) => (
                <option key={label} value={label}>
                  {label} (+${base + (Number(formData.servings) > 50 ? 5 : 0)})
                </option>
              ))}
            </optgroup>
            )}

          </select>

          {/* Custom arrow for consistent dropdown appearance */}
          <div className="pointer-events-none absolute inset-y-0 right-3 flex items-center text-gray-400">
            ▼
          </div>
        </div>
      </div>


      {/* Additional Flavors */}
      {formData.baseCakeFlavor?.startsWith("Half & Half") && (

        <div className="mb-6">
          <div className="flex items-center gap-2 mb-2">
            <p className="font-small text-gray-500">
              Select {allowedExtras.classic > 0 && `${allowedExtras.classic} classic `}
              {allowedExtras.premium > 0 && `${allowedExtras.classic > 0 ? "and " : ""}${allowedExtras.premium} premium `}
              flavor{(allowedExtras.classic + allowedExtras.premium) > 1 ? "s" : ""}
            </p>

          </div>

          {/* Classic Flavors */}
          {allowedExtras.classic > 0 && (
            <div className="mb-4">
              <h4 className="text-sm font-semibold mb-1">Classic Flavors</h4>
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 max-w-2xl">
                {classicFlavors.map((flavor) => (
                  <label
                    key={flavor}
                    className={`flex items-center space-x-2 rounded-lg px-2.5 py-1.5 border text-xs sm:text-sm ${extraFlavors.includes(flavor)
                      ? "bg-[#fef2f2] border-[#a62c2b]"
                      : "hover:bg-gray-50"
                      }`}
                  >
                    <input
                      type="checkbox"
                      className="scale-90 accent-candy-primary"
                      checked={extraFlavors.includes(flavor)}
                      onChange={() => toggleFlavor(flavor)}
                      disabled={isExtraDisabled(flavor)}
                    />
                    <span>{flavor}</span>
                  </label>

                ))}
              </div>
            </div>
          )}

          {/* Premium Flavors */}
          {allowedExtras.premium > 0 && (
            <div>
              <h4 className="text-sm font-semibold mb-1">Premium Flavors</h4>
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 max-w-2xl">
                {premiumFlavors.map((flavor) => (
                  <label
                    key={flavor}
                    className={`flex items-center space-x-2 rounded-lg px-2.5 py-1.5 border text-xs sm:text-sm ${extraFlavors.includes(flavor)
                      ? "bg-[#fef2f2] border-[#a62c2b]"
                      : "hover:bg-gray-50"
                      }`}
                  >
                    <input
                      type="checkbox"
                      className="scale-90 accent-candy-primary"
                      checked={extraFlavors.includes(flavor)}
                      onChange={() => toggleFlavor(flavor)}
                      disabled={isExtraDisabled(flavor)}
                    />
                    <span>{flavor}</span>
                  </label>

                ))}
              </div>
            </div>
          )}
        </div>
      )}



      {/* Filling Visual Cards */}
      <div className="mb-6">
        <div className="flex items-center gap-2 mb-2">
          <h3 className="block font-semibold text-gray-700">Filling Flavor</h3>
          <InfoTooltip>
            <CakeFillingSVG />
            <p className="text-center text-[11px] leading-snug">
              <strong>Fillings</strong> are placed between cake layers to add flavor and moisture.
              Choose a fruity or creamy center for extra deliciousness.
            </p>
          </InfoTooltip>
        </div>

        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-2 sm:gap-3">
          {fillingOptions.map(({ name, img, price }) => {
            const selected = formData.fillingFlavor === name;
            return (
              <label
                key={name}
                className={`cursor-pointer p-2 rounded-md border flex flex-col items-center text-[11px] transition-all text-center ${selected ? 'border-candy-primary bg-candy-background shadow-sm' : 'border-candy-border bg-white hover:border-candy-primary'
                  }`}
              >
                <input
                  type="radio"
                  name="fillingFlavor"
                  value={name}
                  checked={selected}
                  onChange={(e) => setFormData((prev) => ({ ...prev, fillingFlavor: e.target.value }))}
                  className="hidden"
                />
                {img ? (
                  <img src={img} alt={name} className="w-8 h-8 object-contain mb-1" />
                ) : (
                  <span className="text-2xl mb-1">🚫</span>
                )}
                <div className="font-medium leading-tight">{name}</div>
                {price > 0 && (
                  <div className="text-[10px] text-gray-500">
                    +${price + (Number(formData.servings) > 50 ? 5 : 0)}
                  </div>
                )}

              </label>
            );
          })}
        </div>
      </div>

      {/* Frosting */}
      <div className="mb-6">
        <div className="flex items-center gap-2 mb-2">
          <h3 className="block font-semibold text-gray-700">Frosting</h3>
          <InfoTooltip>
            <FrostingSVG />
            <p className="text-center text-[11px] leading-snug">
              <strong>Frosting</strong> is the outer covering. Choose your favorite flavor.
            </p>
          </InfoTooltip>
        </div>

        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-2 sm:gap-3">
          {frostingOptions.map(({ name, color, desc }) => {
            const selected = formData.frosting === name;
            return (
              <label
                key={name}
                className={`cursor-pointer p-2 rounded-md border flex flex-col items-center text-[11px] transition-all text-center ${selected
                  ? 'border-candy-primary bg-candy-background shadow-sm'
                  : 'border-candy-border bg-white hover:border-candy-primary'
                  }`}
              >
                <input
                  type="radio"
                  name="frosting"
                  value={name}
                  checked={selected}
                  onChange={() => setFormData((prev) => ({ ...prev, frosting: name }))}
                  className="hidden"
                />
                <div
                  className="w-6 h-4 rounded-full border border-gray-400 mb-1"
                  style={{ backgroundColor: color }}
                ></div>
                <div className="font-medium leading-tight">{name}</div>
                <div className="text-[10px] text-gray-500">{desc}</div>
              </label>
            );
          })}
        </div>

      </div>

      {/* Decoration */}
      <div className="mb-6">
        <div className="flex items-center gap-2 mb-2">
          <h3 className="block font-semibold text-gray-700">Decoration Style</h3>
          <InfoTooltip>
            <p className="text-center text-[11px] leading-snug">
              Choose a decorative exterior style for your cake. You can choose from dots, floral, scrollwork, and festive styles.
            </p>
          </InfoTooltip>
        </div>

        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-2 sm:gap-3">
          {decorationOptions.map(({ name, value, img }) => {
            const selected = formData.decorationStyle?.startsWith(value);
            return (
              <label
                key={value}
                className={`cursor-pointer flex flex-col items-center justify-center text-center text-xs p-2 rounded-md border transition-all
  ${selected ? 'border-candy-primary bg-candy-background shadow-md' : 'border-candy-border bg-candy-background hover:border-candy-primary'}`}
              >
                <input
                  type="radio"
                  name="decorationStyle"
                  value={value}
                  checked={selected}
                  onChange={(e) => setFormData((prev) => ({ ...prev, decorationStyle: e.target.value }))}
                  className="hidden"
                />
                {img ? (
                  typeof img === "string" ? (
                    <img src={img} alt={name} className="w-12 h-12 object-contain mb-1" />
                  ) : (
                    img // this renders your <NoDecorationSVG />
                  )
                ) : (
                  <span className="text-3xl mb-1">🚫</span>
                )}

                <span>{name}</span>
              </label>
            );
          })}
        </div>
      </div>
      </>
      )}


      {!isCupcakes && (
      <>
      <div className="mb-6">
        {formData.decorationStyle === "floral" || formData.decorationStyle.startsWith("floral:") ? (
          <div className="mt-4 relative">
            <label className="block mb-1 text-sm font-medium">Floral Theme</label>
            <select
              value={floralType}
              onChange={(e) => setFloralDecoration(e.target.value)}
              className={`appearance-none w-full p-3 border border-candy-border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-candy-primary bg-white shadow-sm `}
            >
              <option value="" disabled hidden>Select a floral theme</option>
              {["spring", "summer", "fall", "pastel"].map((option) => (
                <option key={option} value={option}>{option}</option>
              ))}
            </select>
            {/* <div className="pointer-events-none absolute top-1/2 right-3 transform -translate-y-1/2 flex items-center text-gray-400">
            ▼
          </div> */}
          </div>
        ) : null}

        {["scroll-all-over", "scroll-work"].some((style) => formData.decorationStyle === style || formData.decorationStyle.startsWith(`${style}:`)) && (
          <div className="mt-4 relative">
            <label className="block mb-1 text-sm font-medium">Decoration Color</label>
            <select
              value={scrollColor}
              onChange={(e) => setScrollColorDecoration(e.target.value)}
              className={`appearance-none w-full p-3 border border-candy-border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-candy-primary bg-white shadow-sm `}
            >
              <option value="" disabled hidden>Select a color</option>
              {colorOptions.map((color) => (
                <option key={color} value={color.toLowerCase()}>{color}</option>
              ))}
            </select>
            {/* <div className="pointer-events-none absolute top-1/2 right-3 transform -translate-y-1/2 flex items-center text-gray-400">
            ▼
          </div> */}
          </div>
        )}


        {formData.decorationStyle === "dots" || formData.decorationStyle.startsWith("dots:") ? (
          <div className="mt-4">
            <label className="block mb-1 text-sm font-medium">Select up to 2 dot colors <span className="text-gray-500">(at least one required)</span></label>
            <div className="grid grid-cols-3 sm:grid-cols-4 gap-3">
              {colorOptions.map((color) => (
                <label
                  key={color}
                  className={`flex items-center space-x-2 rounded-md px-2 py-1 border text-sm cursor-pointer ${dotsColors.includes(color.toLowerCase()) ? "bg-[#fef2f2] border-[#a62c2b]" : "hover:bg-gray-50"}`}
                >
                  <input
                    type="checkbox"
                    checked={dotsColors.includes(color.toLowerCase())}
                    onChange={() => handleDotsColorCheckbox(color.toLowerCase())}
                    className="accent-candy-primary"
                    disabled={!dotsColors.includes(color.toLowerCase()) && dotsColors.length >= 2}
                  />
                  <span>{color}</span>
                </label>
              ))}
            </div>
          </div>
        ) : null}


        {formData.decorationStyle === "swirls-dots" || formData.decorationStyle.startsWith("swirls-dots:") ? (
          <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="relative">
              <label className="block mb-1 text-sm font-medium">Dots Color</label>
              <select
                value={dotsColor1}
                onChange={(e) => {
                  const val = e.target.value;
                  setDotsColor1(val);
                  setSwirlsDotsDecoration(val, dotsColor2);
                }}
                className={`appearance-none w-full p-3 border border-candy-border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-candy-primary bg-white shadow-sm `}
              >
                <option value="" disabled hidden>Select color</option>
                {colorOptions.map((color) => (
                  <option key={color} value={color.toLowerCase()}>{color}</option>
                ))}
              </select>
              {/* <div className="pointer-events-none absolute top-1/2 right-3 transform -translate-y-1/2 flex items-center text-gray-400">
              ▼
            </div> */}

            </div>
            <div className="relative">
              <label className="block mb-1 text-sm font-medium">Swirls Color</label>
              <select
                value={dotsColor2}
                onChange={(e) => {
                  const val = e.target.value;
                  setDotsColor2(val);
                  setSwirlsDotsDecoration(dotsColor1, val);
                }}
                className={`appearance-none w-full p-3 border border-candy-border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-candy-primary bg-white shadow-sm `}
              >
                <option value="" disabled hidden>Select color</option>
                {colorOptions.map((color) => (
                  <option key={color} value={color.toLowerCase()}>{color}</option>
                ))}
              </select>
              {/* <div className="pointer-events-none absolute top-1/2 right-3 transform -translate-y-1/2 flex items-center text-gray-400">
              ▼
            </div> */}

            </div>
          </div>
        ) : null}
      </div>

      {/* Cake Topper */}
      <div className="mb-6">
        <div className="flex items-center gap-2 mb-2">
          <h3 className="block font-semibold text-gray-700">Cake Message</h3>
          <InfoTooltip>
            <p className="text-center text-[11px] leading-snug">
              Choose a message option. You can add custom text or instructions.
            </p>
          </InfoTooltip>
        </div>

        {/* Topper Type Dropdown */}
        <select
          value={topperType}
          onChange={(e) => {
            const val = e.target.value;
            setFormData((prev) => ({
              ...prev,
              cakeTopper: val === "None" ? "None" : `${val}: `
            }));
          }}

          className="appearance-none w-full p-3 border border-candy-border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-candy-primary bg-white shadow-sm 'text-gray-900'"
        >
          <option value="" disabled hidden>Select a cake topper</option>
          <option value="None">None</option>
          <option value="Plain Text">Plain Text (hand written in icing)</option>

          <option value="Provided">Provided by Customer</option>
          <option value="2D Pre-Cut Message">2D Pre-Cut Message (+$15)</option>
          <option value="Custom Cake Topper">Custom 2D Message (Starts at $20)</option>
        </select>

        {/* Dynamic Inputs */}


        {topperType === "Plain Text" && (
          <>
            <input
              type="text"
              maxLength={40}
              value={customTopperText}
              placeholder="e.g. Happy 18th Birthday Sam"
              onChange={(e) => {
                const value = e.target.value;
                setFormData((prev) => ({
                  ...prev,
                  cakeTopper: `Plain Text: ${value}`
                }));
              }}
              className="mt-2 w-full p-2 border rounded-md text-sm"
            />

            <div className="mt-1 text-xs text-red-600 italic">
              This is the exact sentence that will be written on top of the cake.
            </div>
            <div className="mt-1 text-xs text-gray-500 flex justify-between items-center">
              <span>{customTopperText.length}/40 characters</span>
            </div>
          </>
        )}

        {topperType === "Provided" && (
          <textarea
            rows={3}
            placeholder="Instructions (Optional) e.g. Please leave topper on the side."
            onChange={(e) =>
              setFormData((prev) => ({
                ...prev,
                cakeTopper: `${topperType}: ${e.target.value}`
              }))
            }
            className="mt-2 w-full p-3 border rounded-md text-sm"
          />
        )}

        {topperType === "2D Pre-Cut Message" && (
          <select
            value={formData.cakeTopper?.split(": ")[1]?.split(" (")[0] || ""}
            onChange={(e) => {
              const selectedMessage = e.target.value;
              setFormData((prev) => ({
                ...prev,
                cakeTopper: `2D Pre-Cut Message: ${selectedMessage}${topperColorFinish ? ` (${topperColorFinish})` : ""}`
              }));
            }}
            className="mt-2 appearance-none w-full p-3 border border-candy-border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-candy-primary bg-white shadow-sm 'text-gray-900'"
          >
            <option value="" disabled hidden>Select a message</option>
            {[
              "Happy Birthday",
              "Happy Anniversary",
              "Congratulations",
              "It's a Boy",
              "It's a Girl",
              "Mr & Mrs"
            ].map((msg) => (
              <option key={msg} value={msg}>
                {msg}
              </option>
            ))}
          </select>
        )}

        {topperType === "Custom Cake Topper" && (
          <>
            <input
              type="text"
              maxLength={50}
              value={customTopperText}
              placeholder="e.g. Riya’s Sweet 16"
              onChange={(e) => {
                const value = e.target.value;
                setFormData((prev) => ({
                  ...prev,
                  cakeTopper: `Custom Cake Topper: ${value}${topperColorFinish ? ` (${topperColorFinish})` : ""}`
                }));
              }}
              className="mt-2 w-full p-2 border rounded-md text-sm"
            />

            <div className="mt-1 text-xs text-gray-400 flex justify-between items-center">
              <span>{customTopperText.trim().split(/\s+/).filter(Boolean).length}/5 words</span>
              <span className="ml-auto text-right">
                {(() => {
                  const wordCount = customTopperText.trim().split(/\s+/).filter(Boolean).length;
                  if (wordCount === 0) return "No message entered yet";
                  if (wordCount <= 3) return "Price: $20 for short message (≤3 words)";
                  if (wordCount === 4) return "Price: $25 for 4-word message";
                  return "Price: $30 for 5-word message";
                })()}
              </span>
            </div>
          </>
        )}

        {(topperType === "2D Pre-Cut Message" || topperType === "Custom Cake Topper") && (
          <div className="mt-4">
            <label className="block mb-2 text-sm font-medium">Topper Color & Finish</label>
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3">
              {topperColorFinishOptions.map((option) => {
                const isSelected = topperColorFinish === option.value;
                return (
                  <div
                    key={option.value}
                    onClick={() => {
                      setTopperColorFinish(option.value);
                    }}
                    className={`w-full h-10 rounded-md cursor-pointer flex items-center justify-center text-center text-xs font-medium 
              transition-all border ${isSelected ? "border-candy-primary bg-candy-background" : "border-gray-300 hover:border-candy-primary"
                      }`}
                  >
                    {option.label}
                  </div>
                );
              })}
            </div>
          </div>
        )}

      </div>
      </>
      )}


      <div className="text-center my-6">
        <div className="inline-block px-6 py-3 text-lg font-bold bg-white border-2 border-[#a62c2b] text-[#a62c2b] rounded-md">
          Total Price: ${formData.price}
        </div>
      </div>

      {error && (
        <div className="text-red-600 text-sm font-medium text-center mb-4">
          {error}
        </div>
      )}


      <div className="flex flex-col-reverse sm:flex-row sm:justify-between gap-3">
        <button onClick={prevStep} className="w-full sm:w-auto bg-gray-300 px-6 py-2.5 rounded-md hover:bg-gray-400 transition">
          ← Back
        </button>
        <button
          onClick={() => {
            if (skipValidation) return nextStep();

            // Base required fields
            if (!isCupcakes && (
              !formData.baseCakeFlavor ||
              !formData.fillingFlavor ||
              !formData.frosting ||
              !formData.decorationStyle ||
              !topperType
            )) {
              setError('Please complete all required fields.');
              return;
            }
            if (isCupcakes) {
              const cupcakeErr = validateCupcakeUnits(
                cupcakeUnitConfigs,
                Number(formData.cupcakeDozens || 0),
                cupcakeConfigMode
              );
              if (cupcakeErr) {
                setError(cupcakeErr);
                return;
              }
            }

            // Half & Half validation
            if (!isCupcakes && formData.baseCakeFlavor.startsWith("Half & Half")) {
              const expected = allowedExtrasMap[formData.baseCakeFlavor];
              const counts = { classic: 0, premium: 0 };
              extraFlavors.forEach((flavor) => {
                const type = getFlavorType(flavor);
                if (type) counts[type]++;
              });

              if (
                counts.classic !== expected.classic ||
                counts.premium !== expected.premium
              ) {
                setError(`Please select ${expected.classic} classic and ${expected.premium} premium flavor(s).`);
                return;
              }
            }

            // Decoration validation
            if (!isCupcakes && (
              formData.decorationStyle === "floral" ||
              formData.decorationStyle.startsWith("floral:")
            )) {
              if (!floralType) {
                setError("Please select a floral theme.");
                return;
              }
            }

            if (!isCupcakes && (
              ["scroll-all-over", "scroll-work"].some(style =>
                formData.decorationStyle === style ||
                formData.decorationStyle.startsWith(`${style}:`)
              )
            )) {
              if (!scrollColor) {
                setError("Please select a decoration color.");
                return;
              }
            }

            if (!isCupcakes && (
              formData.decorationStyle.startsWith("dots") &&
              dotsColors.length < 1
            )) {
              setError("Please select at least one dot color.");
              return;
            }


            if (!isCupcakes && (
              formData.decorationStyle === "swirls-dots" ||
              formData.decorationStyle.startsWith("swirls-dots:")
            )) {
              if (!dotsColor1 || !dotsColor2) {
                setError("Please select both swirl and dot colors.");
                return;
              }
            }

            // Topper Validation
            if (!isCupcakes && topperType === "Custom Cake Topper") {
              const wordCount = customTopperText.trim().split(/\s+/).filter(Boolean).length;
              if (!customTopperText.trim()) {
                setError("Please enter a message for the custom topper.");
                return;
              }
              if (wordCount > 5) {
                setError("Topper message must not exceed 5 words.");
                return;
              }
            }

            if (!isCupcakes && topperType === 'Plain Text') {
              if (!customTopperText.trim()) {
                setError('Please enter the exact message to be written on the cake.');
                return;
              }
            }


            if (!isCupcakes && topperType === "2D Pre-Cut Message") {
              if (!formData.cakeTopper || !formData.cakeTopper.split(":")[1]?.trim()) {
                setError("Please select a message for the topper.");
                return;
              }
            }

            // Validate topper color/finish
            if (!isCupcakes && (
              (topperType === "2D Pre-Cut Message" || topperType === "Custom Cake Topper") &&
              !topperColorFinish
            )) {
              setError("Please select a topper color and finish.");
              return;
            }


            setError('');
            nextStep();
          }}

          className="w-full sm:w-auto bg-[#a62c2b] text-white px-6 py-2.5 rounded-md hover:bg-[#8a1f1c] transition"
        >
          Next ➔
        </button>

      </div>
    </div>
  );
};

export default StepFourDecorations;